import { Consent, ServiceRequest } from '@medplum/fhirtypes';
import { OrderSubState } from '../lims-types';
import { ARKON } from '@consts/systems.consts';
import { CONSENT_STATUS_TAG_CODES } from '@/consts/consent.consts';
import {
  ORDER_SUBSTATE_I18N_KEYS,
  OrderSubstateI18nKey,
} from '../constants/order.consts';

// -----------------------------------------------------------------------------
export interface SubstateHistoryEntry {
  substate: OrderSubState;
  occurredAt: string; // ISO 8601
}

// -----------------------------------------------------------------------------
export const getAllOrderSubStates = (): OrderSubState[] => {
  return [
    OrderSubState.OrderCreated,
    OrderSubState.LabProcess,
    OrderSubState.Report,
    OrderSubState.FollowUpTasks,
    OrderSubState.Completed,
  ];
};

// -----------------------------------------------------------------------------
export const getSubStateStepIndex = (
  subState: OrderSubState | null
): number => {
  if (!subState) {
    return 0;
  }

  const allStates = getAllOrderSubStates();
  return allStates.indexOf(subState);
};

// -----------------------------------------------------------------------------
export const updateOrderSubState = (
  serviceRequest: ServiceRequest,
  newSubState: OrderSubState
): ServiceRequest => {
  const updatedServiceRequest = { ...serviceRequest };

  if (!updatedServiceRequest.meta) {
    updatedServiceRequest.meta = {};
  }

  if (!updatedServiceRequest.meta.tag) {
    updatedServiceRequest.meta.tag = [];
  }

  updatedServiceRequest.meta.tag = updatedServiceRequest.meta.tag.filter(
    (tag) => tag.system !== ARKON.SD.LAB_ORDER_SUBSTATE
  );

  updatedServiceRequest.meta.tag.push({
    system: ARKON.SD.LAB_ORDER_SUBSTATE,
    code: newSubState,
  });

  return updatedServiceRequest;
};

// -----------------------------------------------------------------------------
export const getOrderSubState = (
  serviceRequest: ServiceRequest
): OrderSubState | null => {
  const substateTags = serviceRequest.meta?.tag?.filter(
    (tag) => tag.system === ARKON.SD.LAB_ORDER_SUBSTATE
  );

  if (substateTags && substateTags.length > 0) {
    return substateTags[0].code as OrderSubState;
  }

  return null;
};

// -----------------------------------------------------------------------------
// Returns the i18n key; callers should do t(key)
export const getSubStateLabel = (
  subState: OrderSubState
): OrderSubstateI18nKey => {
  return ORDER_SUBSTATE_I18N_KEYS[subState];
};

// -----------------------------------------------------------------------------
/**
 * Returns true if the Consent resource has been answered (permit or deny).
 * Reads from the category coding with the ARKON consent-status-tag system.
 * @param consent - The Consent resource to check.
 * @returns True if consent has been answered.
 */
export const hasConsentAnswered = (consent: Consent | undefined): boolean => {
  if (!consent) {
    return false;
  }
  const statusCode = consent.category
    ?.flatMap((c) => c.coding ?? [])
    .find(
      (coding) => coding.system === CONSENT_STATUS_TAG_CODES.PERMIT.system
    )?.code;
  return (
    statusCode === CONSENT_STATUS_TAG_CODES.PERMIT.code ||
    statusCode === CONSENT_STATUS_TAG_CODES.DENY.code
  );
};
